import {Address, Order, Prescription, UserAddress} from 'types';

// Helper function to format order number
const formatOrderNumber = (order: Order): string => {
  if (!order?.orders?.[0]?.sequential_order_number) {
    return '';
  }
  return order?.orders?.[0]?.sequential_order_number?.toString() || '';
};

// Helper function to format address
const formatAddress = (address: Address): string => {
  if (!address) {
    return 'No Address';
  }
  return address.display_address || '';
};

// Helper function to get customer name
const getCustomerName = (order: Order): string => {
  if (order.customer?.user) {
    return `${order.customer.user.first_name} ${order.customer.user.last_name}`;
  }
  return 'Unknown Customer';
};

// Helper function to get customer phone
const getCustomerPhone = (order: Order): string => {
  return order.customer?.user?.phone || 'No phone';
};

/**
 * Helper function to get customer address
 * @param address - The address to get the display address for
 * @returns The display address for the customer
 */
export const getCustomerAddress = (address?: UserAddress): string => {
  if (!address) {
    return '';
  }
  const displayAddress = address?.display_address;
  const unitNumber = address?.unit_address;
  if (displayAddress) {
    return `${displayAddress}${unitNumber ? `, ${unitNumber}` : ''}`;
  }
  const valueArray = [
    address?.address_line_1,
    address?.city,
    address?.state,
    address?.zipcode,
    address?.unit_address,
  ];

  return valueArray.filter(Boolean).join(', ');
};

// Helper function to calculate payment totals
const calculatePaymentTotals = (items: Prescription[]) => {
  const totals = items.reduce(
    (sum, item) => {
      const paid = Number.isNaN(parseFloat(item.paid))
        ? 0
        : parseFloat(item.paid);
      const amountDue = Number.isNaN(parseFloat(item.amount_due))
        ? 0
        : parseFloat(item.amount_due);
      const cashPrice = Number.isNaN(Number(item.cash_price))
        ? 0
        : Number(item.cash_price);
      return {
        totalAmount: sum.totalAmount + cashPrice,
        totalPaid: sum.totalPaid + paid,
        amountDue: sum.amountDue + amountDue,
      };
    },
    {totalAmount: 0, totalPaid: 0, amountDue: 0}
  );
  return totals;
};
// Helper function to calculate payment totals
const calculatePayableTotals = (items: Prescription[]) => {
  const totals = items.reduce(
    (sum, item) => {
      const paid = Number.isNaN(parseFloat(item.payable_paid!))
        ? 0
        : parseFloat(item.payable_paid!);
      const amountDue = Number.isNaN(parseFloat(item.payable_amount_due!))
        ? 0
        : parseFloat(item.payable_amount_due!);
      const cashPrice = Number.isNaN(Number(item.cash_price))
        ? 0
        : Number(item.cash_price);
      return {
        totalAmount: sum.totalAmount + cashPrice,
        totalPaid: sum.totalPaid + paid,
        amountDue: sum.amountDue + amountDue,
      };
    },
    {totalAmount: 0, totalPaid: 0, amountDue: 0}
  );
  return totals;
};

// Helper function to get service information
const getServiceInfo = (order: Order) => {
  const serviceItem = order.orders?.find(
    (item) => item.items[0]?.drug?.offering_type === 'service'
  );

  if (serviceItem) {
    const service = serviceItem.items[0];
    return {
      title: service.drug?.name || 'Service',
      hours: (service as any)?.quantity || 0,
      rate: parseFloat(service.drug?.price || '0'),
      total: parseFloat((service as any)?.amount_due || '0'),
      id: serviceItem.items[0]?.drug?.id || 0,
    };
  }

  return null;
};

// Helper function to get products
const getProducts = (order: Order) => {
  const productItems =
    order.orders?.filter(
      (item) => item.items[0]?.drug?.offering_type === 'product'
    ) || [];

  return productItems.map((item) => ({
    id: item.items[0]?.drug?.id || 0,
    name: item.items[0]?.drug?.name || 'Unknown Product',
    price: parseFloat(item.items[0]?.drug?.price || '0'),
    quantity: 1,
    total: parseFloat(item.items[0]?.drug?.price || '0'),
  }));
};

const getProductsByUniqueOrders = (orders: Order[], selectedOrder: Order) => {
  const productItems =
    orders?.filter(
      (item) =>
        item?.sequential_order_number === selectedOrder?.sequential_order_number
    ) || [];
  return productItems.map((item) => ({
    id: item.items[0]?.drug?.id || 0,
    name: item.items[0]?.drug?.name || 'Unknown Product',
    price: parseFloat(item.items[0]?.drug?.price || '0'),
    quantity: 1,
    total: parseFloat(item.items[0]?.drug?.price || '0'),
  }));
};

const getPriority = (order: Order) => {
  let foundPriority = null;
  order.orders?.forEach((order) => {
    if (order.priority) {
      if (order.priority === 'high') {
        foundPriority = 'high';
        return;
      } else if (order.priority === 'medium') {
        foundPriority = 'medium';
        return;
      } else if (order.priority === 'low') {
        foundPriority = 'low';
        return;
      }
    }
  });
  return foundPriority || null;
};

export {
  formatOrderNumber,
  formatAddress,
  getCustomerName,
  getCustomerPhone,
  calculatePaymentTotals,
  calculatePayableTotals,
  getServiceInfo,
  getProducts,
  getPriority,
  getProductsByUniqueOrders,
};
